// Global Types
import type {
  $Translate,
} from 'common.language';
import type {
  $FullRegattaResultTable,
  $Model,
} from 'common.data';

// Global Enums
import {
  webPages,
  webSections,
} from 'common.enums';

// Global Components
import * as React from 'react';

// Helpers
import _ from 'lodash';
import ErrorLog from '@vroskus/library-sentry/dist/browser';

// Services
import Services from '../../../../services';

// Components
import Section from '../../../layout/Section';
import Loader from '../../../layout/Loader';
import RegattaResultTable from '../../../common/RegattaResultTable';
import NoData from '../../../common/NoData';

// Types
type $Props = $Translate & {
  item: $Model<'Regatta'>;
};

type $State = {
  data: Array<$FullRegattaResultTable>;
  initialsed: boolean;
  loading: boolean;
};

class ResultsSectionView extends React.Component<$Props, $State> {
  constructor(props: $Props) {
    super(props);

    this.state = {
      data: [],
      initialsed: false,
      loading: false,
    };
  }

  componentDidMount(): void {
    this.setupSocket();
    this.init();
  }

  setStateAsync(state: Partial<$State>): Promise<void> {
    return new Promise((resolve) => {
      this.setState(
        (pervState: $State) => ({
          ...pervState,
          ...state,
        }),
        resolve,
      );
    });
  }

  async getData(): Promise<void> {
    const {
      item,
    } = this.props;

    try {
      await this.setStateAsync({
        loading: true,
      });

      const data = await Services.Api.getRegattaResultsSectionData({
        RegattaId: item.id,
      });

      await this.setStateAsync({
        data,
        loading: false,
      });
    } catch (error) {
      await this.setStateAsync({
        loading: false,
      });

      ErrorLog.exception(error as Error);
    }
  }

  setupSocket(): void {
    const {
      item,
    } = this.props;

    Services.Socket.on({
      action: (data) => {
        const models = ['RegattaResultTable', 'RegattaResultTableEntry', 'RegattaResultTableRace'];

        if (data.RegattaId === item.id && _.includes(
          models,
          data.modelName,
        )) {
          this.getData();
        }
      },
      type: Services.Socket.type.modelChangedEvent,
    });
  }

  async init(): Promise<void> {
    await this.getData();

    await this.setStateAsync({
      initialsed: true,
    });
  }

  render() {
    const {
      item,
      t,
    } = this.props;
    const {
      data,
      initialsed,
      loading,
    } = this.state;

    const hasData: boolean = data.length > 0;

    return (
      <Section
        id={webSections[webPages.regattaPage].results}
        title={t('Results')}
        transparent={hasData === true}
      >
        <Loader
          fade
          loading={initialsed === false && loading === true}
        >
          {hasData === true ? (
            <>
              {data.map((dataItem: $FullRegattaResultTable, index: number) => (
                <div
                  className={`bg-white rounded-lg -mx-5 px-5 pb-2 ${index !== data.length - 1 ? 'mb-3' : '-mb-5'}`}
                  key={dataItem.id}
                >
                  <RegattaResultTable
                    item={dataItem}
                    key={dataItem.id}
                    regatta={item}
                  />
                </div>
              ))}
            </>
          ) : (
            <NoData label={t('No published results yet')} />
          )}
        </Loader>
      </Section>
    );
  }
}

export default ResultsSectionView;
